import React, { useEffect, useRef, useState } from 'react';

import { Box, flameTheme, Flex } from '@lightspeed/flame/Core';
import { IconSmallArrowRight } from '@lightspeed/flame/Icon/SmallArrowRight';
import { Text, TextLink } from '@lightspeed/flame/Text';
import Axios from 'axios';

import { formatCurrency, formatExpiryDate, formatPercentage } from './helpers';
import Modules from 'root/apps/shared_modules/Modules';
import { VisibilityTracker } from 'root/apps/tracking/VisibilityTracker';
import { datadogRum } from 'root/datadog/datadogRum';

import { CapitalBannerLocations, CapitalBannerVersions, CapitalTrackingEvents } from '../enums';
import { useCapitalTracking } from '../hooks';
import { UseOfferData } from '../hooks/useOfferData';
import {
  BackbookBody,
  BackbookButtons,
  BackbookCard,
  BackbookContent,
  BackbookHighlightedPercent,
  BackbookImage,
  BannerButton,
  CapitalContainer,
} from '../styles';
import { ExpiryBadge } from './ExpiryBadge';

const OFFER_URL = '/admin/payments/lightspeed-payments/offer';
const LSPD_CAPITAL_URL = '/payments/lightspeed-payments/merchant-portal/capital';
const REMINDER_DURATION_MS = 6000;
// Composite lifestyle photo with the discount badge baked into the asset.
const BACKBOOK_PHOTO = 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/backbook-image.png';

type Props = {
  offerData: UseOfferData;
  // Accepted-but-ignored for orchestrator interface compat with CapitalHeroBanner.
  capitalOffer?: unknown;
  snapshotDismissalValue?: unknown;
  withVisibilityTracker?: boolean;
};

export const BackbookBanner: React.FC<Props> = ({ offerData, withVisibilityTracker = false }) => {
  const source = 'reports';
  const { trackCapitalEvent } = useCapitalTracking({ source });
  const bannerLocation = CapitalBannerLocations.Top;
  const bannerVersion = CapitalBannerVersions.Backbook;
  const hasReportsCapitalHeapErrorBoundary = Modules.hasModule(
    'reports.capital.heap_error_boundary'
  );
  const [dismissed, setDismissed] = useState(false);
  const reminderTimeout = useRef<number>();

  useEffect(() => () => window.clearTimeout(reminderTimeout.current), []);

  if (!Modules.hasModule('reports.capital.backbook')) return null;

  const { offerId, feeDiscountRate, maxAmount, maxFeeSavingsAmount, daysUntilExpiry, expiresAt } =
    offerData;
  const offerCurrency = offerData.offerCurrency ?? 'USD';

  const trackBannerClick = (extraProps: Record<string, unknown>) => {
    trackCapitalEvent(CapitalTrackingEvents.CapitalBannerClicked, {
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      offer_id: offerId,
      ...extraProps,
    });
  };

  const handleViewOffer = (event?: React.MouseEvent) => {
    event?.preventDefault();
    trackBannerClick({ button_type: 'view' });
    window.PageRouter.goTo(LSPD_CAPITAL_URL);
  };

  const handleDismiss = async () => {
    const payload = { isHidden: true, UUID: offerId };

    try {
      await Axios.put(OFFER_URL, payload);
    } catch (error) {
      // If we can't persist the hidden state, leave the banner in place rather than
      // optimistically dismissing it (and don't fire a dismiss event).
      datadogRum.addError(error, { message: 'Failed to hide Capital banner', ...payload });
      return;
    }

    trackBannerClick({ button_type: 'dismiss', dismissal_type: 'new_offer' });
    offerData.setShowOffer(false);
    setDismissed(true);
    reminderTimeout.current = window.setTimeout(() => setDismissed(false), REMINDER_DURATION_MS);
  };

  // The reminder is checked before the showOffer gating: dismissing clears showOffer in
  // the parent, but we still want to briefly show the reminder before the banner clears.
  if (dismissed) {
    return (
      <BackbookCard>
        <CapitalContainer
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          width="100%"
          px={4}
        >
          <Text as="div" textAlign="center">
            We’ll remind you when a new offer is available.
          </Text>
          <Text as="div" textAlign="center">
            You can always check for available offers in your{' '}
            <TextLink href={LSPD_CAPITAL_URL} onClick={handleViewOffer}>
              Lightspeed Capital
            </TextLink>{' '}
            page.
          </Text>
        </CapitalContainer>
      </BackbookCard>
    );
  }

  if (offerData.isLoading || !offerData.showOffer || !offerData.offerId) return null;

  const percentFormatted = formatPercentage(feeDiscountRate);
  const maxAmountFormatted = formatCurrency(maxAmount, offerCurrency);
  const savingsFormatted = formatCurrency(maxFeeSavingsAmount, offerCurrency);
  const expiryDateFormatted = expiresAt ? formatExpiryDate(expiresAt) : '';

  const bannerContent = (
    <BackbookCard data-test="capital-backbook-banner">
      <BackbookBody>
        <BackbookImage>
          <img src={BACKBOOK_PHOTO} alt="" />
        </BackbookImage>
        <BackbookContent flexDirection={['column', 'column', 'row']}>
          <Box flex="1" minWidth="0">
            <Text
              as="div"
              data-test="backbook-title"
              color="textHeading"
              fontWeight="bold"
              mb={[1, 1, 2]}
              fontSize={['1.2rem', '1.4rem', '1.6rem']}
              lineHeight={flameTheme.lineHeights[5]}
            >
              Up to <var className="notranslate">{maxAmountFormatted}</var> for your business, now{' '}
              <BackbookHighlightedPercent>
                <var className="notranslate">{percentFormatted}</var> off
              </BackbookHighlightedPercent>
              .
            </Text>
            <Flex alignItems="center" flexWrap="wrap" style={{ gap: '12px' }}>
              <Text
                as="div"
                data-test="backbook-subtitle"
                color="gray-600"
                fontSize="text-s"
                lineHeight={flameTheme.lineHeights[3]}
              >
                Save up to <var className="notranslate">{savingsFormatted}</var> if you lock in this
                special rate before{' '}
                <Text as="span" color="gray-600" fontSize="text-s" fontWeight="bold">
                  <var className="notranslate">{expiryDateFormatted}</var>
                </Text>
                .
              </Text>
              <ExpiryBadge daysUntilExpiry={daysUntilExpiry} />
            </Flex>
          </Box>
          <BackbookButtons>
            <BannerButton
              type="button"
              variant="secondary"
              onClick={() => handleViewOffer()}
              data-test="backbook-view-offer"
            >
              View my offer <IconSmallArrowRight color={flameTheme.colors.white} />
            </BannerButton>
            <BannerButton type="button" onClick={handleDismiss} data-test="backbook-maybe-later">
              Maybe later
            </BannerButton>
          </BackbookButtons>
        </BackbookContent>
      </BackbookBody>
    </BackbookCard>
  );

  if (withVisibilityTracker || !hasReportsCapitalHeapErrorBoundary) {
    return (
      <VisibilityTracker
        id="capital-backbook-banner"
        eventName={CapitalTrackingEvents.CapitalBannerViewed}
        triggerOnce={true}
        eventData={{
          source,
          banner_location: bannerLocation,
          banner_version: bannerVersion,
          offer_id: offerId,
        }}
      >
        {bannerContent}
      </VisibilityTracker>
    );
  }

  return bannerContent;
};
